import type {HighlightedEvent} from '@github-ui/timeline-items/HighlightedEvent'
import {graphql, type LoadMoreFn, useLazyLoadQuery, usePaginationFragment} from 'react-relay'

import type {useTimelineHighlightQuery} from './__generated__/useTimelineHighlightQuery.graphql'
import type {NewTimelinePaginationHighlightQuery} from './documents/__generated__/NewTimelinePaginationHighlightQuery.graphql'
import type {
  useTimelineHighlightItems$data,
  useTimelineHighlightItems$key,
} from './documents/__generated__/useTimelineHighlightItems.graphql'
import {highlightTimelineItemsFragment} from './documents/use-timeline-highlight-items'
import type {IssueTimelineItemFragment} from './IssueTimelineItem'

export const isEventHighlighted = (item: IssueTimelineItemFragment, highlight: HighlightedEvent) => {
  // Discard id mismatches
  if (String(item.databaseId) !== highlight.id) return false

  // Discard issue comment highlights if the item is not an issue comment
  if (highlight.prefix === 'issuecomment' && item.__typename !== 'IssueComment') return false

  // Discard event highlights if the item is an issue comment
  if (highlight.prefix === 'event' && item.__typename === 'IssueComment') return false

  return true
}

export type HighlightedData = {
  data: useTimelineHighlightItems$data['timelineItems'] | undefined

  hasPrevious: boolean
  totalBeforeFocus: number
  loadPrevious: LoadMoreFn<NewTimelinePaginationHighlightQuery>

  hasNext: boolean
  totalAfterFocus: number
  loadNext: LoadMoreFn<NewTimelinePaginationHighlightQuery>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const highlightQuery = graphql`
  query useTimelineHighlightQuery(
    $id: ID!
    $focusText: String!
    $after: String
    $before: String
    $first: Int!
    $last: Int
    $focusNeighborCount: Int
  ) {
    node(id: $id) {
      ... on Issue {
        ...useTimelineHighlightItems
          @dangerously_unaliased_fixme
          @arguments(
            after: $after
            first: $first
            focusText: $focusText
            last: $last
            before: $before
            focusNeighborCount: $focusNeighborCount
          )
      }
    }
  }
`

/**
 * Build the variables needed for the highlight query.
 * Exported so callers can preload the query via `fetchQuery` in parallel
 * with other data fetches.
 */
export function buildHighlightQueryVariables(
  issueId: string,
  highlightedEvent: HighlightedEvent | undefined,
): useTimelineHighlightQuery['variables'] {
  return {
    id: issueId,
    focusText: highlightedEvent ? `${highlightedEvent.prefix}-${highlightedEvent.id}` : '',
    first: 1,
    focusNeighborCount: 3,
  }
}

export const useTimelineHighlights = (
  issueId: string,
  highlightedEvent: HighlightedEvent | undefined,
  fetchFromServer: boolean = false,
): HighlightedData => {
  const {node} = useLazyLoadQuery<useTimelineHighlightQuery>(
    highlightQuery,
    buildHighlightQueryVariables(issueId, highlightedEvent),
    // When not fetching from server, read from the Relay store only.
    // When fetching, use store-or-network so a parallel preload (via fetchQuery)
    // can satisfy the request without a redundant network round-trip.
    {fetchPolicy: fetchFromServer ? 'store-or-network' : 'store-only'},
  )

  const {data, hasNext, hasPrevious, loadNext, loadPrevious} = usePaginationFragment<
    NewTimelinePaginationHighlightQuery,
    useTimelineHighlightItems$key
  >(highlightTimelineItemsFragment, node)

  const totalBeforeFocus = data?.timelineItems?.beforeFocusCount || 0
  const totalAfterFocus = data?.timelineItems?.afterFocusCount || 0

  return {
    data: data?.timelineItems,
    hasPrevious,
    totalBeforeFocus,
    loadPrevious,
    hasNext,
    totalAfterFocus,
    loadNext,
  }
}
